"use client";

import React, { useState } from "react";
import Button from "../ui/Button";
import FadeIn from "../ui/FadeIn";

const ZapIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-secondary-darker"
  >
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
  </svg>
);

const LayersIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-secondary-darker"
  >
    <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
    <polyline points="2 17 12 22 22 17"></polyline>
    <polyline points="2 12 12 17 22 12"></polyline>
  </svg>
);

const pricing = [
  {
    name: "One-Time Pass",
    price: "$1.99/",
    unit: "service",
    desc: "Perfect for occasional needs",
    cta: "Pay per use",
    featured: false,
    icon: <ZapIcon />,
  },
  {
    name: "Smart Pass",
    price: "$1.49/",
    unit: "service",
    desc: "Bundle of 5 or 10 services",
    cta: "Get Smart Pass",
    featured: true,
    icon: <LayersIcon />,
  },
  {
    name: "Prime Pass",
    price: "$0.99/",
    unit: "service",
    desc: "10 services monthly + extras discounted",
    cta: "Go Prime",
    featured: false,
    icon: <LayersIcon />,
  },
];

export default function Pricing() {
  const [selectedPlan, setSelectedPlan] = useState("Smart Pass");

  return (
    <section className="bg-zinc-100 px-4 py-10">
      <div className="mx-auto max-w-6xl text-center">
        <FadeIn delay={0.1} direction="up" fullWidth>
          <div className="rounded-2xl bg-primary px-6 py-8 sm:px-8 sm:py-10 text-white shadow-lg">
            <div className="inline-block rounded-lg bg-accent px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold text-primary">
              Limited Time Offer
            </div>
            <h3 className="mt-4 text-2xl sm:text-3xl md:text-4xl font-semibold">
              Start Free. Then Choose What Works for You.
            </h3>
            <p className="mx-auto mt-2 sm:mt-3 max-w-3xl text-sm sm:text-base md:text-lg text-gray-100">
              Everyone gets{" "}
              <span className="font-semibold bg-teal-400 text-primary rounded-full px-4 py-1">
                5 free services
              </span>{" "}
              just for signing up. Ask anything, try it out, and upgrade when
              you&apos;re ready.
            </p>
          </div>
        </FadeIn>

        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {pricing.map((plan, index) => (
            <FadeIn
              key={plan.name}
              delay={0.2 + index * 0.1}
              direction="up"
              fullWidth
            >
              <div
                onClick={() => setSelectedPlan(plan.name)}
                className={`relative group cursor-pointer overflow-hidden flex flex-col rounded-2xl bg-white transition-all duration-500 hover:-translate-y-3 hover:shadow-2xl ${
                  selectedPlan === plan.name
                    ? "ring-2 ring-teal-400 shadow-xl transform scale-[1.02]"
                    : "ring-1 ring-gray-100 shadow-md"
                }`}
              >
                {/* Premium glare effect on hover */}
                <div className="absolute inset-0 bg-linear-to-tr from-white/0 via-white/50 to-white/0 opacity-0 group-hover:opacity-100 group-hover:translate-x-full transition-all duration-1000 z-0 -translate-x-full pointer-events-none" />

                <div className="relative z-10 flex flex-col items-center flex-1 p-6 text-center">
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-teal-400/10 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                    {plan.icon}
                  </div>
                  <h4 className="text-base font-semibold text-primary transition-colors duration-300 group-hover:text-teal-500">
                    {plan.name}
                  </h4>
                  <div className="mt-2 text-5xl font-bold text-gray-900 flex items-baseline justify-center transition-transform duration-300 group-hover:scale-105">
                    {plan.price}
                    <span className="text-lg font-semibold text-gray-800 ml-[2px]">
                      {plan.unit}
                    </span>
                  </div>
                  <p className="mt-3 text-sm text-slate-500 max-w-[80%] leading-relaxed">
                    {plan.desc}
                  </p>
                </div>
                <div
                  className={`relative z-10 w-full p-6 pt-5 transition-colors duration-300 ${
                    selectedPlan === plan.name ? "bg-white" : "bg-slate-50/50 group-hover:bg-slate-50"
                  }`}
                >
                  <Button
                    variant={
                      selectedPlan === plan.name ? "secondary" : "outlineDark"
                    }
                    shape="rounded"
                    className="w-full text-[13px] font-semibold transition-transform duration-300 group-hover:scale-[1.02]"
                  >
                    {plan.cta}
                  </Button>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}
